/**
 * VideoScroll Module - SVG Layer Control + PNG Fallback
 *
 * Scroll-driven animation using panel steps as triggers.
 * Two modes:
 *   - SVG: Inline SVG with named <g> layers, visibility + zoom/pan via CSS
 *   - PNG: Preloaded image stack with crossfade
 *
 * Data Attributes (Container panel):
 *   data-videoscroll="svg|png"   Mode selector
 *   data-vs-layers="id1,id2"    Initial visible layers (SVG mode)
 *   data-vs-image="url"         Initial image (PNG mode)
 *
 * Data Attributes (Step panels):
 *   data-vs-step="N"            Step number
 *   data-vs-layers="id1,id2"    Visible SVG layers for this step
 *   data-vs-zoom="2.5"          CSS scale factor on #vs-stage
 *   data-vs-pan="X,Y"           CSS translate in % from center
 *   data-vs-image="url"         Image URL for this step (PNG mode)
 *   data-vs-transition="0.8"    Custom transition duration (seconds)
 *
 * SVG Naming Convention (from Illustrator):
 *   vs-base         Base map (always visible)
 *   vs-hl-*         Highlight groups
 *   vs-lbl-*        Label groups
 *   vs-callout-*    Callout groups
 *   #vs-stage       Wrapper for zoom/pan transforms
 *
 * Events: Listens to story:panel:activated
 * Registration: core.registerModule('videoscroll', this)
 *
 * File: assets/js/story/story-module-videoscroll.js
 */

(function (window) {
  'use strict';

  // ---- Debug logging ----
  var DEBUG = false;
  function log() {
    if (DEBUG && window.console) {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[VideoScroll]');
      console.log.apply(console, args);
    }
  }


  /* ========================================
     Constructor
     ======================================== */

  function StoryVideoScroll(core) {
    this.core = core;
    this.mode = null;
    this.overlay = null;
    this.stage = null;
    this.allLayers = [];
    this.stepPanels = [];
    this.containerPanel = null;
    this.containerIndex = -1;
    this.sequenceIndices = {};
    this.pngFrames = {};
    this.currentTransition = null;

    // Bound handler for cleanup
    this._onPanelActivated = this.handlePanelActivated.bind(this);

    this.init();
  }


  /* ========================================
     Init
     ======================================== */

  StoryVideoScroll.prototype.init = function () {
    if (!this.core || !this.core.root) return;

    var root = this.core.root;

    // 1. Find container panel (has data-videoscroll attribute)
    var container = root.querySelector('[data-videoscroll]');
    if (!container) {
      log('No [data-videoscroll] found, module inactive');
      return;
    }

    // Walk up to find the actual panel element
    this.containerPanel = this.findPanel(container);
    if (!this.containerPanel) {
      log('Container not inside a panel, aborting');
      return;
    }

    this.mode = container.dataset.videoscroll;
    log('Mode:', this.mode);

    // 2. Create overlay
    this.overlay = document.createElement('div');
    this.overlay.className = 'vs-overlay';
    document.body.appendChild(this.overlay);

    // 3. Setup based on mode
    if (this.mode === 'svg') {
      this.setupSvg(container);
    } else if (this.mode === 'png') {
      this.setupPng(container, root);
    }

    // 4. Collect step panels and build sequence index
    this.buildSequence(root);

    // 5. Register event listeners
    document.addEventListener('story:panel:activated', this._onPanelActivated);

    // 6. Register with core
    this.core.registerModule('videoscroll', this);

    log('Initialized with', Object.keys(this.sequenceIndices).length, 'panels in sequence');
  };


  /* ========================================
     SVG Setup
     ======================================== */

  StoryVideoScroll.prototype.setupSvg = function (container) {
    var svg = container.querySelector('svg');
    if (!svg) {
      log('No <svg> found in container');
      return;
    }

    // Move SVG from panel into overlay
    this.overlay.appendChild(svg);

    // Find #vs-stage for zoom/pan transforms
    this.stage = svg.querySelector('#vs-stage') || svg;

    // Collect all switchable layers
    var selectors = '[id^="vs-hl-"], [id^="vs-lbl-"], [id^="vs-callout-"]';
    this.allLayers = Array.prototype.slice.call(svg.querySelectorAll(selectors));

    log('SVG setup: found', this.allLayers.length, 'switchable layers');
  };


  /* ========================================
     PNG Setup
     ======================================== */

  StoryVideoScroll.prototype.setupPng = function (container, root) {
    // Create stack container
    var stack = document.createElement('div');
    stack.className = 'vs-png-stack';
    this.overlay.appendChild(stack);
    this.stage = stack;

    // Collect all image URLs (from container + step panels)
    var urls = [];
    var containerUrl = container.dataset.vsImage;
    if (containerUrl) urls.push(containerUrl);

    var steps = root.querySelectorAll('[data-vs-step]');
    for (var i = 0; i < steps.length; i++) {
      var url = steps[i].dataset.vsImage;
      if (url && urls.indexOf(url) === -1) {
        urls.push(url);
      }
    }

    // Preload all images and create stack
    for (var j = 0; j < urls.length; j++) {
      var img = document.createElement('img');
      img.className = 'vs-png-frame';
      img.src = urls[j];
      img.alt = '';
      stack.appendChild(img);
      this.pngFrames[urls[j]] = img;
    }

    log('PNG setup: preloading', urls.length, 'images');
  };


  /* ========================================
     Build Sequence (panel index tracking)
     ======================================== */

  StoryVideoScroll.prototype.buildSequence = function (root) {
    var panels = this.core.panels || Array.prototype.slice.call(
      root.querySelectorAll('.story-panel, [data-panel]')
    );

    // Find container panel index
    for (var i = 0; i < panels.length; i++) {
      if (panels[i] === this.containerPanel) {
        this.containerIndex = i;
        this.sequenceIndices[i] = this.containerPanel;
        break;
      }
    }

    // Find all step panels
    var steps = root.querySelectorAll('[data-vs-step]');
    for (var s = 0; s < steps.length; s++) {
      var stepPanel = this.findPanel(steps[s]);
      if (!stepPanel) continue;

      for (var p = 0; p < panels.length; p++) {
        if (panels[p] === stepPanel) {
          this.sequenceIndices[p] = stepPanel;
          this.stepPanels.push(stepPanel);
          break;
        }
      }
    }
  };


  /* ========================================
     Find parent panel element
     ======================================== */

  StoryVideoScroll.prototype.findPanel = function (el) {
    var current = el;
    while (current) {
      if (current.classList && (
        current.classList.contains('story-panel') ||
        current.hasAttribute('data-panel')
      )) {
        return current;
      }
      // If the element itself has data-videoscroll or data-vs-step,
      // and is a direct child of the story wrapper, it IS the panel
      if (current.parentNode && current.parentNode === (this.core && this.core.root)) {
        return current;
      }
      current = current.parentNode;
    }
    return el; // Fallback: treat element itself as panel
  };


  /* ========================================
     Panel Activated Handler
     ======================================== */

  StoryVideoScroll.prototype.handlePanelActivated = function (e) {
    var detail = e.detail;
    var panel = detail.panel;
    var index = detail.index;

    // Check if this panel is in our sequence
    if (!this.sequenceIndices.hasOwnProperty(index)) {
      // Outside sequence — hide overlay
      if (this.overlay.classList.contains('is-active')) {
        this.overlay.classList.remove('is-active');
        log('Overlay hidden (panel', index, 'not in sequence)');
      }
      return;
    }

    // Show overlay
    this.overlay.classList.add('is-active');

    // Read config from panel (or its data-vs child)
    var configEl = panel.querySelector('[data-vs-step]') ||
                   panel.querySelector('[data-videoscroll]') ||
                   panel;

    var layers = (configEl.dataset.vsLayers || '').split(',').filter(Boolean);
    var zoom = parseFloat(configEl.dataset.vsZoom) || 1;
    var panStr = configEl.dataset.vsPan || '0,0';
    var panXY = panStr.split(',').map(function (v) { return parseFloat(v) || 0; });
    var image = configEl.dataset.vsImage || null;
    var transition = configEl.dataset.vsTransition || null;

    // Apply custom transition duration if specified
    if (transition) {
      this.setTransitionDuration(parseFloat(transition));
    } else if (this.currentTransition !== null) {
      this.resetTransitionDuration();
    }

    // Apply state based on mode
    if (this.mode === 'svg') {
      this.applySvgState(layers, zoom, panXY);
    } else if (this.mode === 'png') {
      this.applyPngState(image);
    }

    log('Step activated: panel', index, 'layers:', layers.join(','), 'zoom:', zoom);
  };


  /* ========================================
     Apply SVG State
     ======================================== */

  StoryVideoScroll.prototype.applySvgState = function (layers, zoom, panXY) {
    // 1. Hide all switchable layers
    for (var i = 0; i < this.allLayers.length; i++) {
      this.allLayers[i].removeAttribute('data-vs-visible');
    }

    // 2. Show specified layers
    for (var j = 0; j < layers.length; j++) {
      var layerId = layers[j].trim();
      if (!layerId) continue;

      var el = document.getElementById(layerId);
      if (el) {
        el.setAttribute('data-vs-visible', 'true');
      } else {
        log('Layer not found:', layerId);
      }
    }

    // 3. Apply zoom/pan transform on stage
    if (this.stage) {
      if (zoom === 1 && panXY[0] === 0 && panXY[1] === 0) {
        this.stage.style.transform = '';
      } else {
        this.stage.style.transform =
          'scale(' + zoom + ') translate(' + panXY[0] + '%, ' + panXY[1] + '%)';
      }
    }
  };


  /* ========================================
     Apply PNG State
     ======================================== */

  StoryVideoScroll.prototype.applyPngState = function (imageUrl) {
    if (!imageUrl) return;

    // Hide all frames
    var keys = Object.keys(this.pngFrames);
    for (var i = 0; i < keys.length; i++) {
      this.pngFrames[keys[i]].classList.remove('is-active');
    }

    // Show target frame
    var frame = this.pngFrames[imageUrl];
    if (frame) {
      frame.classList.add('is-active');
    } else {
      log('PNG frame not found:', imageUrl);
    }
  };


  /* ========================================
     Transition Duration Helpers
     ======================================== */

  StoryVideoScroll.prototype.setTransitionDuration = function (seconds) {
    this.currentTransition = seconds;
    var dur = seconds + 's';

    // Apply to all animated elements
    for (var i = 0; i < this.allLayers.length; i++) {
      this.allLayers[i].style.transitionDuration = dur;
    }
    if (this.stage) {
      this.stage.style.transitionDuration = dur;
    }

    // PNG frames
    var keys = Object.keys(this.pngFrames);
    for (var j = 0; j < keys.length; j++) {
      this.pngFrames[keys[j]].style.transitionDuration = dur;
    }
  };

  StoryVideoScroll.prototype.resetTransitionDuration = function () {
    this.currentTransition = null;

    for (var i = 0; i < this.allLayers.length; i++) {
      this.allLayers[i].style.transitionDuration = '';
    }
    if (this.stage) {
      this.stage.style.transitionDuration = '';
    }

    var keys = Object.keys(this.pngFrames);
    for (var j = 0; j < keys.length; j++) {
      this.pngFrames[keys[j]].style.transitionDuration = '';
    }
  };


  /* ========================================
     Destroy
     ======================================== */

  StoryVideoScroll.prototype.destroy = function () {
    document.removeEventListener('story:panel:activated', this._onPanelActivated);

    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }

    this.overlay = null;
    this.stage = null;
    this.allLayers = [];
    this.pngFrames = {};
    this.sequenceIndices = {};

    log('Destroyed');
  };


  /* ========================================
     Expose to window
     ======================================== */

  window.StoryModuleVideoScroll = StoryVideoScroll;

})(window);
